import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { useToast } from 'vue-toastification'
import { getApiBase } from '@/utils/url'
import { detectCountryCode } from '@/utils/currency'
import { getAccessToken, getRefreshToken, getSessionVersion, setAccessToken, clearTokens } from '@/auth/storage'

const api = axios.create({
  baseURL: getApiBase(),
  timeout: 30000,
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error)
    else prom.resolve(token)
  })
  failedQueue = []
}

api.interceptors.request.use(async config => {
  config.sessionVersion = getSessionVersion()
  // Hint backend about user's country for currency resolution
  try {
    const cc = detectCountryCode()
    if (cc) {
      if (!config.headers) config.headers = {}
      config.headers['X-User-Country'] = cc
    }
  } catch (_) {}
  const token = getAccessToken()
  if (!token) {
    if (typeof config.headers?.delete === 'function') config.headers.delete('Authorization')
    else if (config.headers) {
      delete config.headers.Authorization
      delete config.headers.authorization
    }
    return config
  }

  if (!config.headers) config.headers = {}
  if (typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`)
  } else {
    config.headers.Authorization = `Bearer ${token}`
    config.headers.authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config
    const toast = useToast()

    if (!error.response) {
      let id = null
      try { id = Sentry.captureException(error) } catch (_) {}
      const suffix = id ? ` (Error ID: ${id})` : ''
      toast.error(`Network error. Please check your connection and try again.${suffix}`)
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      if (originalRequest?.sessionVersion !== getSessionVersion()) return Promise.reject(error)
      const url = String(originalRequest?.url || '')
      if (url.includes('token/')) {
        return Promise.reject(error)
      }
      originalRequest._retry = true
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            if (originalRequest.sessionVersion !== getSessionVersion()) throw new Error('Session changed during refresh')
            if (!originalRequest.headers) originalRequest.headers = {}
            if (typeof originalRequest.headers.set === 'function') originalRequest.headers.set('Authorization', `Bearer ${token}`)
            else originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch(err => Promise.reject(err))
      }

      isRefreshing = true

      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        processQueue(error, null)
        isRefreshing = false
        clearTokens()
        const clearedVersion = getSessionVersion()
        import('@/stores/auth').then(({ useAuthStore }) => { if (getSessionVersion() === clearedVersion) useAuthStore().logout() }).catch(() => {})
        delete api.defaults.headers.common['Authorization']
        toast.info('Your session has expired. Please sign in again.')
        return Promise.reject(error)
      }

      try {
        const baseURL = getApiBase()
        const response = await axios.post(`${baseURL}token/refresh/`, { refresh: refreshToken }, { timeout: 30000 })
        if (getRefreshToken() !== refreshToken) throw new Error('Session changed during refresh')
        const newAccessToken = response.data.access
        setAccessToken(newAccessToken)
        if (!originalRequest.headers) originalRequest.headers = {}
        if (typeof originalRequest.headers.set === 'function') originalRequest.headers.set('Authorization', `Bearer ${newAccessToken}`)
        else originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        processQueue(null, newAccessToken)
        isRefreshing = false
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        isRefreshing = false
        if (getRefreshToken() === refreshToken) {
          clearTokens()
          const clearedVersion = getSessionVersion()
          import('@/stores/auth').then(({ useAuthStore }) => { if (getSessionVersion() === clearedVersion) useAuthStore().logout() }).catch(() => {})
          delete api.defaults.headers.common['Authorization']
        }
        try { Sentry.captureException(refreshError) } catch (_) {}
        toast.info('Your session has expired. Please sign in again.')
        return Promise.reject(refreshError)
      }
    }

    const status = error.response?.status
    const data = error.response?.data
    const extractMessage = (payload) => {
      if (!payload) return null
      if (typeof payload === 'string') return payload
      if (typeof payload.detail === 'string') return payload.detail
      if (typeof payload.message === 'string') return payload.message
      if (typeof payload === 'object') {
        for (const k of Object.keys(payload)) {
          const v = payload[k]
          if (typeof v === 'string') return v
          if (Array.isArray(v) && v.length && typeof v[0] === 'string') return v[0]
        }
      }
      return null
    }

    let message = extractMessage(data)
    if (!message) {
      if (status >= 500) message = 'Server error. Please try again later.'
      else if (status === 403) message = "You don't have permission to do that."
      else if (status === 404) message = 'Requested resource was not found.'
      else message = 'Request failed. Please try again.'
    }

    if (status >= 500) {
      let id = null
      try { id = Sentry.captureException(error) } catch (_) {}
      const suffix = id ? ` (Error ID: ${id})` : ''
      toast.error(`${message}${suffix}`)
    } else if (status >= 400) {
      toast.error(message)
    }

    return Promise.reject(error)
  }
)

export default api
